/**
 * EditorGizmo: wraps PlayCanvas's built-in Translate/Rotate/Scale gizmos and
 * drives them from `EditorState.mode`.
 *
 * One instance is created for the editor lifetime. It owns a single shared
 * "Gizmo" render layer (added to the camera once) and three gizmo objects —
 * translate / rotate / scale — of which at most one is active (attached + sized)
 * at a time, matching the current `mode` (W=move / E=rotate / R=scale).
 *
 * Coordination with main.ts's canvas mouse handlers:
 *   The gizmo registers its pointer listeners on `app.graphicsDevice.canvas`
 *   (the same #app element) during the SceneEditor constructor — before main.ts
 *   adds its mouse listeners — and pointer events dispatch before mouse events.
 *   So when the user grabs a handle, `transform:start` fires synchronously and
 *   sets `_dragging=true` *before* our `mousedown` runs. main.ts therefore reads
 *   `editor.isGizmoDragging()` and skips camera-look / place / select while a
 *   handle is being dragged. Clicking empty space (no handle hit) does not start
 *   a transform, so normal click-to-select still works.
 *
 * The gizmo mutates the attached marker entity's transform directly during a
 * drag; `SceneEditor` reads the entity back on `transform:move`/`transform:end`
 * to sync the `SceneObject` data, the property panel, and the selection bbox,
 * and to push the history command.
 */

import { Application, Entity, Gizmo, TranslateGizmo, RotateGizmo, ScaleGizmo } from 'playcanvas';
import type { Layer } from 'playcanvas';
import type { EditorMode } from './EditorState';

export interface GizmoCallbacks {
    onTransformStart(id: string): void;
    onTransformMove(id: string): void;
    onTransformEnd(id: string): void;
}

type TransformGizmoInstance = TranslateGizmo | RotateGizmo | ScaleGizmo;

export class EditorGizmo {
    private _app: Application;
    private _layer: Layer;
    private _translate: TranslateGizmo;
    private _rotate: RotateGizmo;
    private _scale: ScaleGizmo;
    private _cb: GizmoCallbacks;

    private _mode: EditorMode = 'select';
    private _id: string | null = null;
    private _node: Entity | null = null;
    private _dragging = false;

    constructor(app: Application, camera: Entity, cb: GizmoCallbacks) {
        this._app = app;
        this._cb = cb;
        this._layer = Gizmo.createLayer(app);
        const cam = camera.camera!;
        this._translate = new TranslateGizmo(cam, this._layer);
        this._rotate = new RotateGizmo(cam, this._layer);
        this._scale = new ScaleGizmo(cam, this._layer);

        const instances: TransformGizmoInstance[] = [this._translate, this._rotate, this._scale];
        for (const g of instances) {
            g.coordSpace = 'world';
            g.on('transform:start', () => {
                this._dragging = true;
                if (this._id) this._cb.onTransformStart(this._id);
            });
            g.on('transform:move', () => {
                if (this._id) this._cb.onTransformMove(this._id);
            });
            g.on('transform:end', () => {
                this._dragging = false;
                if (this._id) this._cb.onTransformEnd(this._id);
            });
        }
    }

    /** True while a gizmo handle is actively being dragged. */
    get isDragging(): boolean { return this._dragging; }

    /** Switch the active gizmo type (W/E/R). Re-attaches to the current node. */
    setMode(mode: EditorMode): void {
        this._mode = mode;
        this._apply();
    }

    /** Attach the gizmo to a marker entity (or detach when null). */
    attach(id: string | null, node: Entity | null): void {
        this._id = id;
        this._node = node;
        this._apply();
    }

    /** Detach from the current node (selection cleared / scene changed). */
    detach(): void {
        this._id = null;
        this._node = null;
        this._apply();
    }

    private _apply(): void {
        // Detach all three so only the active mode's gizmo is attached.
        this._translate.detach();
        this._rotate.detach();
        this._scale.detach();
        if (!this._id || !this._node) return;
        const g = this._modeGizmo();
        if (!g) return; // 'select' / 'place': no transform gizmo shown.
        g.attach([this._node]);
    }

    private _modeGizmo(): TransformGizmoInstance | null {
        switch (this._mode) {
            case 'move': return this._translate;
            case 'rotate': return this._rotate;
            case 'scale': return this._scale;
            default: return null;
        }
    }

    destroy(): void {
        this._translate.destroy();
        this._rotate.destroy();
        this._scale.destroy();
    }
}
